import { useMutation } from "@apollo/client";
import CloseIcon from "@mui/icons-material/Close";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
} from "@mui/material";
import { Button } from "pepsico-ds";
import { useState } from "react";
import { deleteTag } from "../../graphql/mutations/deleteStoreGroup";
import StoreGroupsTable from "./StoreGroupsTable";
import "./storeManagement.scss";

const StoreGroupsListView = ({
  storeGroups,
  loading,
  onViewGroup,
  onAddGroup,
  onEditGroup,
  showToast,
  refetchStoreGroups,
  refetchProgramConfig,
}) => {
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [targetGroup, setTargetGroup] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  const [executeDelete] = useMutation(deleteTag);

  const handleDeleteGroup = (groupName) => {
    setTargetGroup(groupName);
    setDeleteModalOpen(true);
  };

  const handleDeleteClose = () => {
    setDeleteModalOpen(false);
    setTargetGroup(null);
  };

  const handleDeleteConfirm = async () => {
    const target = storeGroups.find((g) => g.name === targetGroup);
    if (target?.storeCount > 0) {
      showToast(
        "error",
        "Cannot Delete Group",
        `"${targetGroup}" has associated stores and cannot be deleted. Remove all stores from this group first.`
      );
      handleDeleteClose();
      return;
    }

    setActionLoading(true);
    try {
      await executeDelete({
        variables: { name: targetGroup, type: "STORE_GROUPS" },
      });
      showToast(
        "success",
        "Group Removed",
        `Store group "${targetGroup}" has been removed`
      );
      if (refetchProgramConfig) refetchProgramConfig();
      if (refetchStoreGroups) refetchStoreGroups();
      handleDeleteClose();
    } catch (error) {
      showToast(
        "error",
        "Delete Failed",
        error.message || "Could not delete the store group"
      );
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <div className="store-groups-list-view">
      <div className="store-groups-list-actions">
        <Button
          text="Create Store Group"
          variant="primary"
          onClick={onAddGroup}
          className="add-store-group-btn"
        />
      </div>
      <StoreGroupsTable
        storeGroups={storeGroups}
        loading={loading}
        onViewGroup={onViewGroup}
        onEditGroup={onEditGroup}
        onDeleteGroup={handleDeleteGroup}
      />

      {/* Delete Store Group Confirmation Modal */}
      <Dialog
        open={deleteModalOpen}
        onClose={handleDeleteClose}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          <Grid container justifyContent="space-between" alignItems="center">
            <Grid item>
              <h3 style={{ margin: 0 }}>Remove store group?</h3>
            </Grid>
            <Grid item>
              <CloseIcon onClick={handleDeleteClose} className="cursor-pointer" />
            </Grid>
          </Grid>
        </DialogTitle>
        <DialogContent>
          <p style={{ margin: 0 }}>
            Are you sure you want to remove this group? This action can&apos;t be
            undone.
          </p>
        </DialogContent>
        <DialogActions>
          <Button
            variant="secondary"
            onClick={handleDeleteClose}
            text="Cancel"
            size="small"
          />
          <Button
            variant="primary"
            onClick={handleDeleteConfirm}
            text="Remove"
            size="small"
            disabled={actionLoading}
          />
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default StoreGroupsListView;
